/**
 * Conditional-question visibility for the Closing Questionnaire (genericTemplate).
 *
 * Template data (see questionWithConditions in
 * src/pages/templateBuilder/questionnaireTemplate/questionnaireTemplate.jsx):
 *
 *   question = {
 *     uuid, type, question, options,
 *     require_response: "true" | "false",
 *     make_conditional: "true" | "false",
 *     conditions: [{ uuid, if, state, value, do, doType, questionId }]
 *   }
 *
 * A condition lives INSIDE the question that gates it. Semantics:
 *   "show `do` (the question identified by `questionId`) when the gating
 *    question's answer `state` `value`"
 *
 * So `state` is the comparator, `value` is the expected answer of the gate,
 * `do` is the target question text and `questionId` is the target question uuid.
 *
 * Two properties this module guarantees:
 *
 *  1. DEFAULT VISIBLE. A question is hidden only when it is explicitly targeted
 *     by a condition that is not currently met. Questions that are neither a
 *     gate nor a target (e.g. "Did you complete a teach back?", "Closing Notes")
 *     always show. The Task Inspection survey flow uses the opposite rule
 *     (only `make_conditional === "true"` questions are roots), which would hide
 *     those questions here - do not copy it.
 *
 *  2. ORDER INDEPENDENT. A revealed question is always rendered AFTER the gate
 *     that revealed it, even when the target is declared earlier in the array.
 *     The template builder has no drag-to-reorder, so a question added later
 *     lands after the questions it needs to gate.
 */

const EQUAL = "is equal to";
const NOT_EQUAL = "is not equal to";
const IS_EMPTY = "is empty";
const IS_FILLED = "is filled";

/** `conditions` is persisted as an array, but older records store "" or an object. */
const toConditions = (question) => {
  const raw = question?.conditions;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((c) => c && typeof c === "object");
  return [];
};

const asText = (value) => (value === null || value === undefined ? "" : String(value).trim());

/**
 * @param {{state?: string, value?: string}} condition
 * @param {string|undefined} gateAnswer  answer given to the gating question
 * @returns {boolean} true when the condition is met
 */
export const evaluateCondition = (condition, gateAnswer) => {
  const state = asText(condition?.state).toLowerCase();
  const value = asText(condition?.value);
  const answer = asText(gateAnswer);

  if (state === EQUAL) {
    // Legacy alias: templates saved as state "Is Equal To" + value "Filled".
    if (value.toLowerCase() === "filled") return answer.length > 0;
    return answer.toLowerCase() === value.toLowerCase();
  }
  if (state === NOT_EQUAL) return answer.toLowerCase() !== value.toLowerCase();
  if (state === IS_EMPTY) return answer.length === 0;
  if (state === IS_FILLED) return answer.length > 0;

  // "Please choose" / blank / unknown comparator: the condition is not
  // configured, so it must not reveal anything.
  return false;
};

const isRequired = (question) =>
  asText(question?.require_response ?? question?.requireResponse).toLowerCase() === "true";

/** Map every uuid to its question so targets can be resolved by uuid. */
const indexByUuid = (questions) => {
  const byUuid = new Map();
  for (const question of questions) {
    if (question?.uuid) byUuid.set(String(question.uuid), question);
  }
  return byUuid;
};

/** uuid of the question a condition points at, or null if it is unusable. */
const resolveTargetUuid = (condition, byUuid) => {
  const id = condition?.questionId === null || condition?.questionId === undefined
    ? ""
    : String(condition.questionId).trim();
  if (id && byUuid.has(id)) return id;

  // Fallback: the builder only fills questionId when it can match the `do` text
  // against a question, so older/hand-edited records may only have the text.
  const text = asText(condition?.do).toLowerCase();
  if (!text) return null;
  for (const [uuid, question] of byUuid) {
    if (asText(question?.question).toLowerCase() === text) return uuid;
  }
  return null;
};

/**
 * Questions that should be rendered, in the order they should be rendered in.
 *
 * @param {Array<object>} questions  template.questions
 * @param {Object<string, string>} [answers]  { [questionUuid]: answer }
 * @returns {Array<object>}
 */
export const orderVisibleQuestions = (questions, answers = {}) => {
  const list = Array.isArray(questions) ? questions.filter(Boolean) : [];
  if (!list.length) return [];

  const answerFor = (question) => {
    if (!question?.uuid) return "";
    const raw = answers?.[String(question.uuid)];
    return raw === null || raw === undefined ? "" : String(raw);
  };

  const byUuid = indexByUuid(list);

  // target uuid -> the gate that reveals it. First gate wins.
  const targetParent = new Map();
  // gate uuid -> resolvable conditions
  const conditionsByGate = new Map();
  // Walk up the parent chain to detect a circular reference. Template data is
  // hand-authored, so a -> b -> a is possible; a cycle must not be able to hide
  // the whole questionnaire, so the offending edge is dropped.
  const wouldCycle = (gateUuid, targetUuid) => {
    let cursor = gateUuid;
    const seen = new Set();
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      if (cursor === targetUuid) return true;
      cursor = targetParent.get(cursor);
    }
    return false;
  };
  for (const question of list) {
    const conditions = toConditions(question);
    if (!question?.uuid || !conditions.length) continue;
    const gateUuid = String(question.uuid);
    const resolved = [];
    for (const condition of conditions) {
      const targetUuid = resolveTargetUuid(condition, byUuid);
      if (!targetUuid || targetUuid === gateUuid) continue;
      if (targetParent.has(targetUuid)) continue; // first gate wins
      if (wouldCycle(gateUuid, targetUuid)) continue;
      targetParent.set(targetUuid, gateUuid);
      resolved.push({ condition, targetUuid });
    }
    if (resolved.length) conditionsByGate.set(gateUuid, resolved);
  }

  // 1) Default visible: everything that nobody gates.
  const visible = new Set();
  for (const question of list) {
    if (!question?.uuid) continue;
    if (targetParent.has(String(question.uuid))) continue;
    visible.add(String(question.uuid));
  }

  // 2) Reveal pass, repeated to a fixpoint so chained gates work no matter
  //    where the chain is declared in the array.
  let changed = true;
  while (changed) {
    changed = false;
    for (const question of list) {
      const gateUuid = String(question?.uuid ?? "");
      if (!gateUuid || !visible.has(gateUuid)) continue;
      const conditions = conditionsByGate.get(gateUuid);
      if (!conditions) continue;
      const gateAnswer = answerFor(question);
      for (const { condition, targetUuid } of conditions) {
        if (visible.has(targetUuid)) continue;
        if (evaluateCondition(condition, gateAnswer)) {
          visible.add(targetUuid);
          changed = true;
        }
      }
    }
  }

  // 3) Display order: a gate is emitted before everything it reveals, recursively.
  //    `emitting` guards against circular references.
  const ordered = [];
  const emitted = new Set();
  const emit = (uuid) => {
    if (emitted.has(uuid)) return;
    emitted.add(uuid);
    const question = byUuid.get(uuid);
    if (!question) return;
    ordered.push(question);
    for (const { condition, targetUuid } of conditionsByGate.get(uuid) || []) {
      if (visible.has(targetUuid)) emit(targetUuid);
    }
  };

  for (const question of list) {
    const uuid = String(question?.uuid ?? "");
    if (!uuid || !visible.has(uuid)) continue;
    if (targetParent.has(uuid)) continue; // emitted by its gate
    emit(uuid);
  }
  // Safety net: never drop a visible question because of a malformed graph.
  for (const question of list) {
    const uuid = String(question?.uuid ?? "");
    if (uuid && visible.has(uuid)) emit(uuid);
  }

  return ordered;
};

/** Convenience wrapper: same order as orderVisibleQuestions. */
export const getVisibleQuestions = orderVisibleQuestions;

/**
 * Required questions, among the given (already ordered / filtered) questions,
 * that still have no answer.
 */
export const getMissingRequiredQuestions = (questions, answers = {}) => {
  const missing = [];
  for (const question of Array.isArray(questions) ? questions : []) {
    if (!isRequired(question)) continue;
    const uuid = question?.uuid ? String(question.uuid) : "";
    if (asText(answers?.[uuid]).length === 0) {
      missing.push(question?.question || "Required field");
    }
  }
  return missing;
};

export { isRequired };
